import { z } from 'zod'
import { agentStateSchema, threadActivitySchema } from './chat-events'

/**
 * Project hub (PLAN-081 step 1): one socket per open project that carries
 * everything project-wide and ephemeral — who is online and where, task
 * drag hints, "the tasks/meeting/thread list changed, refetch" events and
 * the busy-thread glow. Nothing here is a source of truth: rows live in the
 * ProjectStore, the hub only fans out change notifications.
 */

export const hubPresenceSchema = z.object({
  /** Connection id — one user may have several tabs open. */
  connId: z.string(),
  userId: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  currentRoute: z.string(),
  currentRouteKey: z.string().nullable(),
  /** Task the user is dragging on the kanban board, if any. */
  dragging: z.object({ taskId: z.string(), fromCol: z.string() }).nullable(),
})
export type HubPresence = z.infer<typeof hubPresenceSchema>

/** Change notifications published by the worker (procedures, pipelines, agent). */
export const projectEventSchema = z.discriminatedUnion('t', [
  /** Any task, subtask, comment, attachment or column changed — refetch. */
  z.object({ t: z.literal('tasks:changed'), taskId: z.string().optional() }),
  z.object({ t: z.literal('meeting:changed'), meetingId: z.string() }),
  z.object({ t: z.literal('meetings:changed') }),
  /** A HUB video meeting went live or ended — calendar badge, sidebar dot (PLAN-085). */
  z.object({ t: z.literal('meeting:live'), meetingId: z.string(), live: z.boolean() }),
  /** Chat thread list changed (create/rename/pin/delete/new message). */
  z.object({ t: z.literal('threads:changed') }),
  /** Credits were booked (or a limit/cap changed) — refetch credits.getUsage. */
  z.object({ t: z.literal('credits:changed') }),
  /** The project's own settings changed (name, description, meeting-notes rules) — refetch projects.get. */
  z.object({ t: z.literal('project:changed') }),
  /** Project inbox changed (mail arrived, status/folder moved, rule run) — refetch (PLAN-084). */
  z.object({ t: z.literal('inbox:changed'), emailId: z.string().optional() }),
  /** Project plugins changed (added/removed/toggled, status or last error) — refetch mcp.list (board wwfu). */
  z.object({ t: z.literal('mcp:changed'), serverId: z.string().optional() }),
  /** The agent's state in a chat thread changed (sidebar glow). */
  z.object({
    t: z.literal('thread:activity'),
    threadId: z.string(),
    state: agentStateSchema,
    updatedAt: z.string(),
  }),
])
export type ProjectEvent = z.infer<typeof projectEventSchema>

// ── Client → server ───────────────────────────────────────────────────────

export const hubClientMessageSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('route'),
    currentRoute: z.string().max(2_000),
    currentRouteKey: z.string().max(64).nullable(),
  }),
  z.object({
    t: z.literal('drag'),
    dragging: z.object({ taskId: z.string(), fromCol: z.string() }).nullable(),
  }),
])
export type HubClientMessage = z.infer<typeof hubClientMessageSchema>

// ── Server → client ───────────────────────────────────────────────────────

export const hubServerMessageSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('hello'),
    self: z.string(),
    presence: z.array(hubPresenceSchema),
    activeThreads: z.array(threadActivitySchema),
  }),
  z.object({ t: z.literal('presence'), presence: z.array(hubPresenceSchema) }),
  z.object({ t: z.literal('event'), event: projectEventSchema }),
  z.object({ t: z.literal('error'), message: z.string() }),
])
export type HubServerMessage = z.infer<typeof hubServerMessageSchema>
